import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";

import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";


import { getAuthToken } from "../utils/session"
import {
  ArrowBack as ArrowBackIcon,
  AutoFixHigh as AutoFixHighIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  CloseRounded as CloseRoundedIcon,
  DescriptionRounded as DescriptionRoundedIcon,
  ExpandMore as ExpandMoreIcon,
  Flag as FlagIcon,
  GroupsRounded as GroupsRoundedIcon,
  InsightsRounded as InsightsRoundedIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  RestartAltRounded as RestartAltRoundedIcon,
  SearchRounded as SearchRoundedIcon,
  ShieldRounded as ShieldRoundedIcon,
  TaskAltRounded as TaskAltRoundedIcon,
  TimelineRounded as TimelineRoundedIcon,
  WorkspacePremiumRounded as WorkspacePremiumIcon,
} from "@mui/icons-material";

import axiosInstance from "../axiosInstance";
import { API_ENDPOINTS } from "../config";

const FONT_FAMILY =
  'Inter, Manrope, "SF Pro Display", "Segoe UI", Roboto, Arial, sans-serif';

const COLORS = {
  page: "#F7F9FC",
  pageSoft: "#FFFFFF",
  surface: "#FFFFFF",
  surfaceSoft: "#FBFCFE",
  surfaceStrong: "#FFFFFF",

  border: "rgba(15,36,56,0.10)",
  borderSoft: "rgba(15,36,56,0.07)",
  borderStrong: "rgba(15,36,56,0.16)",

  text: "#172033",
  textStrong: "#102840",
  muted: "#64748B",
  mutedSoft: "#94A3B8",

  primary: "#1E4B81",
  primaryDark: "#102840",
  primarySoft: "#EEF5FF",

  accent: "#D37D36",
  accentDark: "#B6632C",
  accentSoft: "#FFF4EA",

  success: "#14A67A",
  successSoft: "#EAFBF6",

  danger: "#D32F2F",
  dangerSoft: "#FFEBEE",

  purple: "#5B5CE2",
  purpleSoft: "#F0F0FF",

  warning: "#C47A1F",
  warningSoft: "#FFF7E8",

  shadow: "0 18px 48px rgba(15,36,56,0.06)",
  hoverShadow: "0 18px 42px rgba(15,36,56,0.08)",

  background:
    "radial-gradient(circle at 18% 0%, rgba(30,75,129,0.08), transparent 32%), radial-gradient(circle at 92% 8%, rgba(211,125,54,0.08), transparent 28%), linear-gradient(180deg, #F7F9FC 0%, #FFFFFF 48%, #F7F9FC 100%)",
};

const TYPO = {
  pageTitle: 900,
  sectionTitle: 820,
  itemTitle: 760,
  bodyStrong: 680,
  body: 520,
  meta: 650,
  chip: 800,
  button: 820,
};

const panelSx = {
  bgcolor: COLORS.surface,
  border: `1px solid ${COLORS.border}`,
  borderRadius: "22px",
  boxShadow: COLORS.shadow,
};

const secondaryButtonSx = {
  minHeight: 40,
  borderRadius: "13px",
  px: 1.7,
  textTransform: "none",
  fontWeight: TYPO.button,
  fontSize: "0.76rem",
  fontFamily: FONT_FAMILY,
  color: COLORS.primary,
  borderColor: "rgba(30,75,129,0.22)",
  bgcolor: COLORS.surface,

  "&:hover": {
    borderColor: COLORS.primary,
    bgcolor: COLORS.primarySoft,
  },
};

const PROCESS_TYPE_CONFIG = {
  Gobierno: {
    label: "Gobierno",
    color: COLORS.primary,
    bg: COLORS.primarySoft,
    icon: <ShieldRoundedIcon sx={{ fontSize: 18 }} />,
  },
  Gestion: {
    label: "Gestión",
    color: COLORS.accent,
    bg: COLORS.accentSoft,
    icon: <GroupsRoundedIcon sx={{ fontSize: 18 }} />,
  },
  Gestión: {
    label: "Gestión",
    color: COLORS.accent,
    bg: COLORS.accentSoft,
    icon: <GroupsRoundedIcon sx={{ fontSize: 18 }} />,
  },
  Calidad: {
    label: "Calidad",
    color: COLORS.success,
    bg: COLORS.successSoft,
    icon: <TaskAltRoundedIcon sx={{ fontSize: 18 }} />,
  },
};

const TIMEFRAME_CONFIG = [
  {
    key: "cortoPlazo",
    title: "Corto plazo",
    icon: <FlagIcon />,
    color: COLORS.danger,
    bg: COLORS.dangerSoft,
    description: "Acciones urgentes o de alto impacto que conviene abordar primero.",
  },
  {
    key: "medioPlazo",
    title: "Medio plazo",
    icon: <PlayCircleOutlineIcon />,
    color: COLORS.primary,
    bg: COLORS.primarySoft,
    description: "Iniciativas importantes que consolidan la mejora de los procesos.",
  },
  {
    key: "largoPlazo",
    title: "Largo plazo",
    icon: <CheckCircleOutlineIcon />,
    color: COLORS.success,
    bg: COLORS.successSoft,
    description: "Acciones de optimización, madurez y mejora continua.",
  },
];

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const extractTitleFromDescription = (text) => {
  const value = String(text || "").replace(/\s+/g, " ").trim();
  if (!value) return "";

  const patterns = [
    /Consiste en\s+(.+?)(?:,\s+asegurando|\s+asegurando|\.\s+Desde|\.\s+Su prioridad|$)/i,
    /Las tareas? prioritarias? (?:son|es):\s+(.+?)(?:\.|$)/i,
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match?.[1]) return match[1].trim();
  }

  return "";
};

const cleanTaskDescription = (text) => {
  const raw = String(text || "").replace(/\r/g, "").trim();
  if (!raw) return "";

  const normalized = raw.replace(/\s+/g, " ").trim();
  const cutPatterns = [
    /\bPasos(?:\s+de\s+implementación)?\s*:\s*/i,
    /\bEvidencias(?:\s+esperadas)?\s*:\s*/i,
    /\bResponsables(?:\s+sugeridos)?\s*:\s*/i,
    /\bImplicados\s*:\s*/i,
    /\bPrioridad\s*:\s*(Alta|Media|Baja)?/i,
    /\bSu prioridad\b/i,
    /\bse propone situarla\b/i,
  ];

  let end = normalized.length;
  cutPatterns.forEach((pattern) => {
    const match = normalized.match(pattern);
    if (match && typeof match.index === "number") end = Math.min(end, match.index);
  });

  return normalized.slice(0, end).trim();
};

const getTaskTitle = (task, fallbackTasks = []) => {
  if (typeof task === "string") return task.trim() || "Tarea sin título";

  const direct = [
    task?.tituloCompleto,
    task?.tituloOriginal,
    task?.tareaOriginal,
    task?.tarea,
    task?.titulo,
    task?.nombre,
    task?.name,
    task?.title,
    task?.texto,
    task?.label,
    extractTitleFromDescription(task?.descripcion),
    extractTitleFromDescription(task?.descripcionCompleta),
  ];

  for (const value of direct) {
    const clean = String(value || "").replace(/\s+/g, " ").trim();
    if (clean && clean.toLowerCase() !== "tarea sin título") return clean;
  }

  const normalize = (value) => normalizeText(String(value || "").replace(/\s+/g, " ").trim());
  const ids = [task?._id, task?.id, task?.tareaId, task?.respuestaId, task?.respuestaSeleccionadaId]
    .filter(Boolean)
    .map(String);
  const taskDescription = normalize(task?.descripcion || task?.descripcionCompleta);
  const firstStep = normalize(Array.isArray(task?.pasos) ? task.pasos[0] : "");

  const matched = (fallbackTasks || []).find((candidate) => {
    const candidateIds = [
      candidate?._id,
      candidate?.id,
      candidate?.tareaId,
      candidate?.respuestaId,
      candidate?.respuestaSeleccionadaId,
    ]
      .filter(Boolean)
      .map(String);

    if (ids.length && ids.some((id) => candidateIds.includes(id))) return true;

    const candidateDescription = normalize(candidate?.descripcion || candidate?.descripcionCompleta);
    if (taskDescription && candidateDescription) {
      if (taskDescription === candidateDescription) return true;
      if (taskDescription.length > 80 && candidateDescription.includes(taskDescription.slice(0, 80))) return true;
      if (candidateDescription.length > 80 && taskDescription.includes(candidateDescription.slice(0, 80))) return true;
    }

    const candidateFirstStep = normalize(Array.isArray(candidate?.pasos) ? candidate.pasos[0] : "");
    if (firstStep && candidateFirstStep && firstStep === candidateFirstStep) return true;

    return false;
  });

  if (matched && matched !== task) return getTaskTitle(matched, []);

  return "Tarea sin título";
};

const getTaskProcessName = (task) =>
  String(
    task?.procesoNombre ||
      task?.proceso ||
      task?.nombreProceso ||
      task?.processName ||
      ""
  )
    .replace(/\s+/g, " ")
    .trim();

const getTaskFirstStep = (task) =>
  Array.isArray(task?.pasos) && task.pasos.length
    ? String(task.pasos[0] || "").replace(/\s+/g, " ").trim()
    : "";

const getTaskMatchKey = (task, fallbackTasks = []) => {
  const title = getTaskTitle(task, fallbackTasks);
  const processName = getTaskProcessName(task);
  const firstStep = getTaskFirstStep(task);
  const raw = [title, processName, firstStep.slice(0, 120)]
    .filter(Boolean)
    .join("||");

  return normalizeText(raw || JSON.stringify(task || {}));
};

const getTaskStableId = (task, fallback = "") => {
  const raw = getTaskMatchKey(task) || fallback;

  return String(raw || fallback || "task")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 120);
};

const isSameTask = (a, b, fallbackTasks = []) => {
  if (!a || !b) return false;

  const aKey = getTaskMatchKey(a, fallbackTasks);
  const bKey = getTaskMatchKey(b, fallbackTasks);
  if (aKey && bKey && aKey === bKey) return true;

  const aTitle = normalizeText(getTaskTitle(a, fallbackTasks));
  const bTitle = normalizeText(getTaskTitle(b, fallbackTasks));
  const aProcess = normalizeText(getTaskProcessName(a));
  const bProcess = normalizeText(getTaskProcessName(b));

  if (aTitle && bTitle && aTitle === bTitle && (!aProcess || !bProcess || aProcess === bProcess)) {
    return true;
  }

  const aIds = [a?._id, a?.id, a?.tareaId, a?.respuestaId, a?.respuestaSeleccionadaId]
    .filter(Boolean)
    .map(String);
  const bIds = [b?._id, b?.id, b?.tareaId, b?.respuestaId, b?.respuestaSeleccionadaId]
    .filter(Boolean)
    .map(String);

  if (aIds.length && bIds.length && aIds.some((id) => bIds.includes(id))) return true;

  return false;
};

const getProcessTypeConfig = (type) => {
  return (
    PROCESS_TYPE_CONFIG[type] || {
      label: type || "Proceso",
      color: COLORS.primary,
      bg: COLORS.primarySoft,
      icon: <DescriptionRoundedIcon sx={{ fontSize: 18 }} />,
    }
  );
};

const cleanEmbeddedSectionsFromDescription = (value) => {
  let text = String(value || "").trim();

  const markers = [
    /\bPasos\s*:/i,
    /\bEvidencias\s+esperadas\s*:/i,
    /\bResponsables\s+sugeridos\s*:/i,
    /\bPrioridad\s*:/i,
    /\bSu prioridad\b/i,
    /\bse propone situarla\b/i,
  ];

  for (const marker of markers) {
    const match = text.match(marker);
    if (match && match.index > 0) {
      text = text.slice(0, match.index).trim();
    }
  }

  return text;
};

const parseDetailedContent = (fullText) => {
  if (!fullText) {
    return {
      mainDescription: "No hay descripción disponible.",
      steps: [],
      prioridad: null,
    };
  }

  const cleanedFullText = cleanEmbeddedSectionsFromDescription(fullText);
  const lines = String(fullText).split("\n").filter((line) => line.trim() !== "");

  let mainDescription = [];
  let steps = [];
  let prioridad = null;
  let inStepsSection = false;

  for (const line of lines) {
    const cleanLine = line.trim();

    if (cleanLine.toLowerCase().startsWith("pasos:")) {
      inStepsSection = true;
      continue;
    }

    if (/^(evidencias esperadas|responsables sugeridos)\s*:/i.test(cleanLine)) {
      inStepsSection = false;
      continue;
    }

    const priorityMatch = cleanLine.match(/Prioridad:\s*(Alta|Media|Baja)/i);

    if (priorityMatch) {
      prioridad = priorityMatch[1];
      continue;
    }

    if (cleanLine.match(/^\d+\.?\s*/)) {
      inStepsSection = true;
    }

    if (inStepsSection) {
      steps.push(cleanLine.replace(/^\d+\.?\s*/, ""));
    } else {
      mainDescription.push(cleanLine.replace(/Descripción:\s*/i, ""));
    }
  }

  return {
    mainDescription:
      cleanEmbeddedSectionsFromDescription(mainDescription.join(" ").trim()) || cleanedFullText,
    steps,
    prioridad,
  };
};

const RoadmapSkeleton = () => (
  <Box>
    <Skeleton variant="text" width="42%" height={34} sx={{ mb: 0.8 }} />
    <Skeleton variant="text" width="72%" height={22} sx={{ mb: 3 }} />

    <Grid container spacing={1.6} sx={{ mb: 2.5 }}>
      {[...Array(3)].map((_, index) => (
        <Grid item xs={12} md={4} key={index}>
          <Skeleton variant="rounded" height={96} sx={{ borderRadius: 4 }} />
        </Grid>
      ))}
    </Grid>

    <Skeleton variant="rounded" height={430} sx={{ borderRadius: 5 }} />
  </Box>
);

const HeaderSection = ({ title, subtitle, chip, icon }) => (
  <Stack
    direction={{ xs: "column", md: "row" }}
    justifyContent="space-between"
    alignItems={{ xs: "flex-start", md: "center" }}
    spacing={1.6}
    sx={{ mb: 2.2 }}
  >
    <Stack direction="row" spacing={1.2} alignItems="center">
      {icon && (
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: COLORS.primarySoft,
            color: COLORS.primary,
            border: `1px solid ${COLORS.primary}22`,
          }}
        >
          {icon}
        </Avatar>
      )}

      <Box>
        {chip && (
          <Chip
            label={chip}
            size="small"
            sx={{
              height: 23,
              mb: 0.55,
              bgcolor: COLORS.primarySoft,
              color: COLORS.primary,
              border: `1px solid ${COLORS.primary}22`,
              fontWeight: TYPO.chip,
              fontSize: "0.66rem",
              fontFamily: FONT_FAMILY,
            }}
          />
        )}

        <Typography
          sx={{
            color: COLORS.textStrong,
            fontWeight: TYPO.pageTitle,
            fontSize: { xs: "1.15rem", md: "1.35rem" },
            letterSpacing: "-0.04em",
            lineHeight: 1.15,
            fontFamily: FONT_FAMILY,
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            sx={{
              color: COLORS.muted,
              fontWeight: TYPO.body,
              fontSize: "0.78rem",
              lineHeight: 1.55,
              mt: 0.4,
              fontFamily: FONT_FAMILY,
              maxWidth: 880,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  </Stack>
);

const getTaskAreaKey = (task) => {
  const type = String(task?.procesoTipo || task?.tipoProceso || task?.area || "").trim();
  const normalized = normalizeText(type);

  if (normalized.includes("gobierno")) return "Gobierno";
  if (normalized.includes("calidad")) return "Calidad";
  if (normalized.includes("gestion") || normalized.includes("gestión")) return "Gestion";

  const processText = normalizeText(`${task?.proceso || ""} ${task?.procesoNombre || ""}`);
  if (processText.includes("calidad")) return "Calidad";
  if (
    processText.includes("riesgo") ||
    processText.includes("estrategia") ||
    processText.includes("politica")
  ) {
    return "Gobierno";
  }

  return "Gestion";
};

const AREA_LANES = [
  {
    key: "Gobierno",
    label: "Gobierno del dato",
    shortLabel: "Gobierno",
    color: COLORS.primary,
    bg: COLORS.primarySoft,
    icon: <ShieldRoundedIcon sx={{ fontSize: 18 }} />,
  },
  {
    key: "Gestion",
    label: "Gestión del dato",
    shortLabel: "Gestión",
    color: COLORS.accent,
    bg: COLORS.accentSoft,
    icon: <GroupsRoundedIcon sx={{ fontSize: 18 }} />,
  },
  {
    key: "Calidad",
    label: "Calidad del dato",
    shortLabel: "Calidad",
    color: "#667085",
    bg: "#F7F8FA",
    icon: <TaskAltRoundedIcon sx={{ fontSize: 18 }} />,
  },
];

const getPhaseSummary = (planResumen, config) => {
  return config.description;
};

const RoadmapMetricTile = ({ config, tasks, total, index }) => {
  const percent = total > 0 ? Math.round((tasks.length / total) * 100) : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        overflow: "hidden",
        height: "100%",
        p: { xs: 1.4, md: 1.7 },
        borderRadius: "22px",
        bgcolor: COLORS.surface,
        border: `1px solid ${config.color}22`,
        boxShadow: "0 14px 36px rgba(15,36,56,0.06)",
        "&:before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 6,
          background: `linear-gradient(180deg, ${config.color}, ${config.color}88)`,
        },
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Avatar
          sx={{
            width: 42,
            height: 42,
            bgcolor: config.bg,
            color: config.color,
            border: `1px solid ${config.color}33`,
          }}
        >
          {React.cloneElement(config.icon, { sx: { fontSize: 22 } })}
        </Avatar>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              color: config.color,
              fontWeight: 900,
              fontSize: "0.65rem",
              textTransform: "uppercase",
              letterSpacing: 0.6,
              fontFamily: FONT_FAMILY,
            }}
          >
            Horizonte {index + 1}
          </Typography>

          <Typography
            sx={{
              color: COLORS.textStrong,
              fontWeight: TYPO.pageTitle,
              fontSize: "0.98rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            {config.title}
          </Typography>
        </Box>

        <Box sx={{ textAlign: "right" }}>
          <Typography
            sx={{
              color: config.color,
              fontWeight: 950,
              fontSize: "1.65rem",
              lineHeight: 1,
              letterSpacing: "-0.05em",
              fontFamily: FONT_FAMILY,
            }}
          >
            {tasks.length}
          </Typography>

          <Typography
            sx={{
              color: COLORS.muted,
              fontSize: "0.62rem",
              fontWeight: 700,
              fontFamily: FONT_FAMILY,
            }}
          >
            acciones
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mt: 1.25 }}>
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{
            height: 8,
            borderRadius: 99,
            bgcolor: "rgba(15,36,56,0.07)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 99,
              bgcolor: config.color,
            },
          }}
        />

        <Typography
          sx={{
            color: COLORS.muted,
            fontSize: "0.68rem",
            lineHeight: 1.45,
            mt: 0.9,
            fontFamily: FONT_FAMILY,
          }}
        >
          {config.description}
        </Typography>
      </Box>
    </Paper>
  );
};

const RoadmapTaskPill = ({ task, config, fallbackTasks, onTaskClick }) => {
  const title = getTaskTitle(task, fallbackTasks);
  const processName = task?.procesoNombre || task?.proceso || "Proceso";

  return (
    <Tooltip title={title} arrow placement="top">
      <Box
        role={onTaskClick ? "button" : undefined}
        tabIndex={onTaskClick ? 0 : undefined}
        onClick={() => onTaskClick?.(task)}
        onKeyDown={(event) => {
          if (!onTaskClick) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onTaskClick(task);
          }
        }}
        sx={{
          display: "grid",
          gridTemplateColumns: "10px 1fr auto",
          gap: 0.95,
          alignItems: "start",
          p: 1,
          borderRadius: "16px",
          bgcolor: "#FFFFFF",
          border: `1px solid ${COLORS.border}`,
          boxShadow: "0 10px 22px rgba(15,36,56,0.045)",
          minWidth: 0,
          cursor: onTaskClick ? "pointer" : "default",
          transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
          "&:hover": {
            borderColor: `${config.color}55`,
            boxShadow: "0 16px 32px rgba(15,36,56,0.08)",
            transform: onTaskClick ? "translateY(-2px)" : "none",
          },
          "&:focus-visible": {
            outline: `3px solid ${config.color}22`,
            borderColor: config.color,
          },
        }}
      >
        <Box
          sx={{
            width: 7,
            height: "100%",
            minHeight: 42,
            borderRadius: 99,
            background: `linear-gradient(180deg, ${config.color}, ${config.color}66)`,
          }}
        />

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: COLORS.textStrong,
              fontWeight: 820,
              fontSize: "0.72rem",
              lineHeight: 1.35,
              fontFamily: FONT_FAMILY,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              color: COLORS.muted,
              fontWeight: 650,
              fontSize: "0.61rem",
              mt: 0.32,
              fontFamily: FONT_FAMILY,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {processName}
          </Typography>
        </Box>

        <Chip
          label="Detalle"
          size="small"
          sx={{
            height: 20,
            display: { xs: "none", xl: "inline-flex" },
            bgcolor: "#F8FAFC",
            color: COLORS.muted,
            border: `1px solid ${COLORS.border}`,
            fontSize: "0.56rem",
            fontWeight: 850,
            fontFamily: FONT_FAMILY,
          }}
        />
      </Box>
    </Tooltip>
  );
};

const RoadmapCell = ({ config, area, tasks, fallbackTasks, onTaskClick }) => {
  return (
    <Box
      sx={{
        minHeight: 172,
        p: 1.05,
        bgcolor: COLORS.surface,
        borderLeft: `1px solid ${COLORS.borderSoft}`,
        borderTop: `1px solid ${COLORS.borderSoft}`,
        position: "relative",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.9 }}>
        <Typography
          sx={{
            color: COLORS.muted,
            fontWeight: 900,
            fontSize: "0.62rem",
            textTransform: "uppercase",
            letterSpacing: 0.45,
            fontFamily: FONT_FAMILY,
          }}
        >
          {config.title}
        </Typography>

        <Chip
          size="small"
          label={`${tasks.length} ${tasks.length === 1 ? "acción" : "acciones"}`}
          sx={{
            height: 21,
            bgcolor: tasks.length ? "#F8FAFC" : COLORS.surfaceSoft,
            color: tasks.length ? COLORS.textStrong : COLORS.mutedSoft,
            border: `1px solid ${COLORS.border}`,
            fontWeight: 850,
            fontSize: "0.58rem",
            fontFamily: FONT_FAMILY,
          }}
        />
      </Stack>

      {tasks.length ? (
        <Stack spacing={0.75} sx={{ maxHeight: 310, overflowY: "auto", pr: 0.35 }}>
          {tasks.map((task, index) => (
            <RoadmapTaskPill
              key={task._id || task.id || task.tareaId || `${area.key}-${config.key}-${index}`}
              task={task}
              config={config}
              fallbackTasks={fallbackTasks}
              onTaskClick={onTaskClick}
            />
          ))}
        </Stack>
      ) : (
        <Box
          sx={{
            minHeight: 108,
            display: "grid",
            placeItems: "center",
            borderRadius: "16px",
            border: `1px dashed ${COLORS.borderStrong}`,
            bgcolor: "#FBFCFE",
            color: COLORS.mutedSoft,
            fontSize: "0.68rem",
            fontWeight: 750,
            fontFamily: FONT_FAMILY,
            textAlign: "center",
            px: 1,
          }}
        >
          Sin acciones asignadas
        </Box>
      )}
    </Box>
  );
};

const RoadmapMatrix = ({ plan, planResumen, fallbackTasks, onTaskClick }) => {
  const grouped = useMemo(() => {
    const result = {};

    AREA_LANES.forEach((area) => {
      result[area.key] = {};
      TIMEFRAME_CONFIG.forEach((phase) => {
        result[area.key][phase.key] = [];
      });
    });

    TIMEFRAME_CONFIG.forEach((phase) => {
      (plan?.[phase.key] || []).forEach((task) => {
        const areaKey = getTaskAreaKey(task);
        const safeAreaKey = result[areaKey] ? areaKey : "Gestion";
        result[safeAreaKey][phase.key].push(task);
      });
    });

    return result;
  }, [plan]);

  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        overflow: "hidden",
        mb: 2.2,
        background: "linear-gradient(180deg, #FFFFFF 0%, #F9FBFD 100%)",
      }}
    >
      <Box
        sx={{
          p: { xs: 1.4, md: 1.8 },
          borderBottom: `1px solid ${COLORS.border}`,
          bgcolor: "rgba(248,251,255,0.92)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1}
        >
          <Box>
            <Typography
              sx={{
                color: COLORS.textStrong,
                fontWeight: 950,
                fontSize: "1.05rem",
                fontFamily: FONT_FAMILY,
              }}
            >
              Matriz de implantación por ámbito y horizonte
            </Typography>

            <Typography
              sx={{
                color: COLORS.muted,
                fontSize: "0.73rem",
                lineHeight: 1.55,
                mt: 0.35,
                fontFamily: FONT_FAMILY,
              }}
            >
              Plan organizado por ámbito y horizonte temporal. Las acciones se consultan desde la propia matriz y mantienen su ficha operativa asociada.
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
            {AREA_LANES.map((area) => (
              <Chip
                key={area.key}
                icon={<Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: area.color }} />}
                label={area.label}
                sx={{
                  bgcolor: area.bg,
                  color: area.color,
                  border: `1px solid ${area.color}22`,
                  fontWeight: TYPO.chip,
                  fontFamily: FONT_FAMILY,
                }}
              />
            ))}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ display: { xs: "none", lg: "block" }, overflowX: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "150px repeat(3, minmax(300px, 1fr))",
            minWidth: 1080,
          }}
        >
          <Box
            sx={{
              p: 1,
              bgcolor: COLORS.surfaceSoft,
              borderBottom: `1px solid ${COLORS.border}`,
              fontFamily: FONT_FAMILY,
            }}
          >
            <Typography
              sx={{
                color: COLORS.muted,
                fontWeight: 900,
                fontSize: "0.66rem",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Ámbito
            </Typography>
          </Box>

          {TIMEFRAME_CONFIG.map((phase) => (
            <Box
              key={phase.key}
              sx={{
                p: 1.25,
                bgcolor: COLORS.surface,
                borderLeft: `1px solid ${COLORS.borderSoft}`,
                borderBottom: `1px solid ${COLORS.border}`,
              }}
            >
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    bgcolor: "#fff",
                    color: phase.color,
                    border: `1px solid ${phase.color}33`,
                  }}
                >
                  {React.cloneElement(phase.icon, { sx: { fontSize: 18 } })}
                </Avatar>

                <Box>
                  <Typography
                    sx={{
                      color: phase.color,
                      fontWeight: 950,
                      fontSize: "0.83rem",
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {phase.title}
                  </Typography>

                  <Typography
                    sx={{
                      color: COLORS.muted,
                      fontSize: "0.62rem",
                      fontFamily: FONT_FAMILY,
                      lineHeight: 1.35,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {getPhaseSummary(planResumen, phase)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))}

          {AREA_LANES.map((area) => (
            <React.Fragment key={area.key}>
              <Box
                sx={{
                  p: 1,
                  minHeight: 154,
                  display: "flex",
                  alignItems: "center",
                  bgcolor: `linear-gradient(180deg, ${area.bg} 0%, #FFFFFF 100%)`,
                  borderLeft: `5px solid ${area.color}`,
                  borderBottom: `1px solid ${COLORS.borderSoft}`,
                  boxShadow: `inset -1px 0 0 ${COLORS.borderSoft}`,
                }}
              >
                <Stack direction="column" spacing={0.65} alignItems="flex-start">
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "#fff",
                      color: area.color,
                      border: `1px solid ${area.color}33`,
                    }}
                  >
                    {area.icon}
                  </Avatar>

                  <Box>
                    <Typography
                      sx={{
                        color: COLORS.textStrong,
                        fontWeight: 900,
                        fontSize: "0.74rem",
                        lineHeight: 1.2,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {area.label}
                    </Typography>

                    <Typography
                      sx={{
                        color: COLORS.muted,
                        fontWeight: 700,
                        fontSize: "0.64rem",
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {TIMEFRAME_CONFIG.reduce(
                        (sum, phase) => sum + grouped[area.key][phase.key].length,
                        0
                      )}{" "}
                      acciones
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {TIMEFRAME_CONFIG.map((phase) => (
                <RoadmapCell
                  key={`${area.key}-${phase.key}`}
                  config={phase}
                  area={area}
                  tasks={grouped[area.key][phase.key]}
                  fallbackTasks={fallbackTasks}
                  onTaskClick={onTaskClick}
                />
              ))}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      <Stack spacing={1.1} sx={{ display: { xs: "flex", lg: "none" }, p: 1.25 }}>
        {TIMEFRAME_CONFIG.map((phase) => (
          <Paper
            key={phase.key}
            elevation={0}
            sx={{
              borderRadius: "18px",
              border: `1px solid ${phase.color}22`,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 1.1,
                bgcolor: COLORS.surface,
                borderBottom: `1px solid ${phase.color}22`,
              }}
            >
              <Stack direction="row" spacing={0.9} alignItems="center">
                <Avatar sx={{ width: 32, height: 32, bgcolor: "#fff", color: phase.color }}>
                  {React.cloneElement(phase.icon, { sx: { fontSize: 18 } })}
                </Avatar>

                <Typography
                  sx={{
                    color: phase.color,
                    fontWeight: 950,
                    fontSize: "0.9rem",
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {phase.title}
                </Typography>
              </Stack>
            </Box>

            <Stack spacing={1} sx={{ p: 1.1 }}>
              {AREA_LANES.map((area) => (
                <Box key={`${phase.key}-${area.key}`}>
                  <Typography
                    sx={{
                      color: area.color,
                      fontWeight: 900,
                      fontSize: "0.68rem",
                      mb: 0.7,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {area.shortLabel} · {grouped[area.key][phase.key].length}
                  </Typography>

                  <Stack spacing={0.65}>
                    {grouped[area.key][phase.key].slice(0, 8).map((task, index) => (
                      <RoadmapTaskPill
                        key={task._id || task.id || task.tareaId || index}
                        task={task}
                        config={phase}
                        fallbackTasks={fallbackTasks}
                        onTaskClick={onTaskClick}
                      />
                    ))}

                    {grouped[area.key][phase.key].length > 8 && (
                      <Typography
                        sx={{
                          color: COLORS.muted,
                          fontSize: "0.68rem",
                          fontWeight: 750,
                          fontFamily: FONT_FAMILY,
                        }}
                      >
                        + {grouped[area.key][phase.key].length - 8} acciones más en esta matriz
                      </Typography>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Paper>
  );
};

const TimeframeRoadmap = ({ cuestionarioId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [plan, setPlan] = useState(null);
  const [roadmapTaskCatalog, setRoadmapTaskCatalog] = useState([]);

  useEffect(() => {
    const token = getAuthToken();

    const fetchPlan = async () => {
      setLoading(true);
      setError("");

      try {
        const [planResponse, catalogResponse] = await Promise.all([
          axiosInstance.get(API_ENDPOINTS.organizarPorPlazo(cuestionarioId), {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axiosInstance
            .get(API_ENDPOINTS.todasLasTareas(cuestionarioId), {
              headers: { Authorization: `Bearer ${token}` },
            })
            .catch(() => ({ data: [] })),
        ]);

        setRoadmapTaskCatalog(Array.isArray(catalogResponse.data) ? catalogResponse.data : []);
        setPlan(planResponse.data);
      } catch (err) {
        setError("No se pudo generar el roadmap. Es posible que aún se esté procesando.");
      } finally {
        setLoading(false);
      }
    };

    fetchPlan();
  }, [cuestionarioId]);

  const totalRoadmapTasks = useMemo(() => {
    if (!plan) return 0;
    return TIMEFRAME_CONFIG.reduce((sum, item) => sum + (plan[item.key]?.length || 0), 0);
  }, [plan]);

  const planResumen = plan?.planResumen || null;

  const handleRoadmapTaskClick = (task) => {
    const payload = {
      task,
      matchKey: getTaskMatchKey(task, roadmapTaskCatalog),
      stableId: getTaskStableId(task),
    };

    window.__dqpaPendingTaskDetail = payload;
    window.dispatchEvent(new CustomEvent("dqpa:openTaskDetail", { detail: payload }));

    setTimeout(() => {
      const element = document.getElementById(`task-detail-${payload.stableId}`);
      const detailSection = document.getElementById("dqpa-detalle-tareas");
      (element || detailSection)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 180);
  };

  if (loading) return <RoadmapSkeleton />;

  if (error) {
    return (
      <Paper elevation={0} sx={{ ...panelSx, p: 3, textAlign: "center" }}>
        <Alert
          severity="warning"
          sx={{
            borderRadius: "16px",
            bgcolor: COLORS.warningSoft,
            border: `1px solid ${COLORS.warning}22`,
            color: COLORS.text,
            fontFamily: FONT_FAMILY,
          }}
        >
          {error}
        </Alert>

        <Button
          variant="outlined"
          size="small"
          sx={{ ...secondaryButtonSx, mt: 2 }}
          onClick={() => window.location.reload()}
        >
          Reintentar
        </Button>
      </Paper>
    );
  }

  return (
    <Box>
      <HeaderSection
        icon={<TimelineRoundedIcon />}
        chip="Roadmap estratégico"
        title="Roadmap de implantación"
        subtitle="Plan de implantación por horizonte y ámbito, conectado con la ficha operativa de cada acción."
      />

      <Grid container spacing={1.4} sx={{ mb: 2 }}>
        {TIMEFRAME_CONFIG.map((config, index) => (
          <Grid item xs={12} md={4} key={config.key}>
            <RoadmapMetricTile
              config={config}
              tasks={plan?.[config.key] || []}
              total={totalRoadmapTasks}
              index={index}
            />
          </Grid>
        ))}
      </Grid>

      <RoadmapMatrix
        plan={plan}
        planResumen={planResumen}
        fallbackTasks={roadmapTaskCatalog}
        onTaskClick={handleRoadmapTaskClick}
      />
    </Box>
  );
};

const DetailedTasksView = ({ cuestionarioId, userRole }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [allTasksFlat, setAllTasksFlat] = useState([]);
  const [selectedProcessType, setSelectedProcessType] = useState("Gobierno");
  const [expandedProcessPanel, setExpandedProcessPanel] = useState([]);
  const [expandedTaskPanel, setExpandedTaskPanel] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const token = getAuthToken();

    const fetchTasks = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await axiosInstance.get(API_ENDPOINTS.todasLasTareas(cuestionarioId), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setAllTasksFlat(response.data || []);
      } catch (err) {
        setError("No se pudieron cargar las tareas detalladas.");
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [cuestionarioId]);

  useEffect(() => {
    const openTaskDetail = (payload = {}) => {
      const requestedTask = payload.task;
      const requestedKey = payload.matchKey || getTaskMatchKey(requestedTask);
      if (!requestedTask || !allTasksFlat.length) return false;

      const matchedTask =
        allTasksFlat.find((candidate) => getTaskMatchKey(candidate) === requestedKey) ||
        allTasksFlat.find((candidate) => isSameTask(candidate, requestedTask));

      if (!matchedTask) return false;

      const type = getTaskAreaKey(matchedTask);
      const processName = matchedTask.procesoNombre || matchedTask.proceso || "Proceso sin nombre";
      const stableId = getTaskStableId(matchedTask);

      setSelectedProcessType(type);
      setExpandedProcessPanel((prev) =>
        prev.includes(processName) ? prev : [...prev, processName]
      );
      setExpandedTaskPanel(stableId);

      setTimeout(() => {
        document.getElementById(`task-detail-${stableId}`)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 280);

      return true;
    };

    const handleOpenTaskDetail = (event) => {
      const opened = openTaskDetail(event.detail || {});
      if (opened) window.__dqpaPendingTaskDetail = null;
    };

    window.addEventListener("dqpa:openTaskDetail", handleOpenTaskDetail);

    if (window.__dqpaPendingTaskDetail) {
      const opened = openTaskDetail(window.__dqpaPendingTaskDetail);
      if (opened) window.__dqpaPendingTaskDetail = null;
    }

    return () => window.removeEventListener("dqpa:openTaskDetail", handleOpenTaskDetail);
  }, [allTasksFlat]);

  const stats = useMemo(() => {
    const countByType = (type) => allTasksFlat.filter((task) => task.procesoTipo === type).length;

    return {
      total: allTasksFlat.length,
      Gobierno: countByType("Gobierno"),
      Gestion: countByType("Gestion"),
      Calidad: countByType("Calidad"),
    };
  }, [allTasksFlat]);

  const filteredTasks = useMemo(() => {
    let tasks = allTasksFlat.filter((task) => task.procesoTipo === selectedProcessType);

    if (searchQuery.trim()) {
      const normalized = normalizeText(searchQuery);

      tasks = tasks.filter((task) => {
        return normalizeText(
          `${getTaskTitle(task, allTasksFlat)} ${task.procesoNombre || ""} ${
            task.procesoTipo || ""
          }`
        ).includes(normalized);
      });
    }

    return tasks;
  }, [allTasksFlat, selectedProcessType, searchQuery]);

  const groupedTasks = useMemo(() => {
    const grouped = {};

    filteredTasks.forEach((task) => {
      const processName = task.procesoNombre || "Proceso sin nombre";

      if (!grouped[processName]) {
        grouped[processName] = {
          name: processName,
          tasks: [],
        };
      }

      grouped[processName].tasks.push(task);
    });

    return Object.values(grouped);
  }, [filteredTasks]);

  const handleTaskAccordionChange = (taskId) => (event, isExpanded) => {
    setExpandedTaskPanel(isExpanded ? taskId : null);
  };

  const handleProcessAccordionChange = (panelName) => (event, isExpanded) => {
    setExpandedProcessPanel((prev) =>
      isExpanded ? [...prev, panelName] : prev.filter((name) => name !== panelName)
    );
  };

  if (loading) {
    return (
      <Box sx={{ py: 2 }}>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[...Array(4)].map((_, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Skeleton variant="rounded" height={96} sx={{ borderRadius: 4 }} />
            </Grid>
          ))}
        </Grid>

        <Skeleton variant="rounded" height={420} sx={{ borderRadius: 5 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper elevation={0} sx={{ ...panelSx, p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: "16px" }}>
          {error}
        </Alert>
      </Paper>
    );
  }

  return (
    <Box id="dqpa-detalle-tareas">
      <HeaderSection
        icon={<TaskAltRoundedIcon />}
        chip="Plan de mejora"
        title="Tareas recomendadas por área y proceso"
        subtitle="Plan de acción organizado por áreas para mejorar el gobierno, la gestión y la calidad de los datos."
      />

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total"
            value={stats.total}
            icon={<TaskAltRoundedIcon />}
            color={COLORS.textStrong}
            bg={COLORS.surfaceSoft}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Gobierno"
            value={stats.Gobierno}
            icon={<ShieldRoundedIcon />}
            color={COLORS.primary}
            bg={COLORS.primarySoft}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Gestión"
            value={stats.Gestion}
            icon={<GroupsRoundedIcon />}
            color={COLORS.accent}
            bg={COLORS.accentSoft}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Calidad"
            value={stats.Calidad}
            icon={<CheckCircleOutlineIcon />}
            color={COLORS.success}
            bg={COLORS.successSoft}
          />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.4, md: 1.7 }, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.3}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <ToggleButtonGroup
            value={selectedProcessType}
            exclusive
            onChange={(event, value) => {
              if (value) setSelectedProcessType(value);
            }}
            sx={{
              bgcolor: COLORS.surfaceSoft,
              border: `1px solid ${COLORS.border}`,
              borderRadius: "14px",
              overflow: "hidden",

              "& .MuiToggleButtonGroup-grouped": {
                border: "none",
              },

              "& .MuiToggleButton-root": {
                px: 1.6,
                py: 0.8,
                textTransform: "none",
                fontWeight: TYPO.button,
                fontSize: "0.74rem",
                fontFamily: FONT_FAMILY,
                color: COLORS.muted,
                borderRadius: "0 !important",
              },

              "& .MuiToggleButton-root.Mui-selected": {
                bgcolor: COLORS.primarySoft,
                color: COLORS.primary,
              },
            }}
          >
            {["Gobierno", "Calidad", "Gestion"].map((type) => {
              const config = getProcessTypeConfig(type);
              const label = type === "Gestion" ? "Gestión" : type;

              return (
                <ToggleButton key={type} value={type}>
                  <Stack direction="row" spacing={0.7} alignItems="center">
                    <Box sx={{ color: config.color, display: "flex" }}>{config.icon}</Box>
                    <span>
                      {label} ({stats[type] || 0})
                    </span>
                  </Stack>
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>

          <TextField
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Buscar tarea o proceso..."
            size="small"
            sx={{
              width: { xs: "100%", md: 380 },

              "& .MuiOutlinedInput-root": {
                height: 40,
                borderRadius: "13px",
                bgcolor: COLORS.surface,
                fontSize: "0.76rem",
                fontFamily: FONT_FAMILY,

                "& fieldset": {
                  borderColor: COLORS.border,
                },

                "&:hover fieldset": {
                  borderColor: COLORS.primary,
                },

                "&.Mui-focused fieldset": {
                  borderColor: COLORS.primary,
                },
              },

              "& input::placeholder": {
                color: COLORS.muted,
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: COLORS.muted, fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")}>
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Stack>
      </Paper>

      <List disablePadding>
        {groupedTasks.length === 0 ? (
          <EmptyState
            icon={<SearchRoundedIcon />}
            title="No hay tareas para este criterio"
            text="Prueba a cambiar el área seleccionada o el texto de búsqueda."
          />
        ) : (
          <Stack spacing={1.2}>
            {groupedTasks.map((group) => {
              const groupId = group.name;
              const isExpanded = expandedProcessPanel.includes(groupId);
              const typeConfig = getProcessTypeConfig(selectedProcessType);

              return (
                <MuiAccordion
                  key={group.name}
                  expanded={isExpanded}
                  onChange={handleProcessAccordionChange(groupId)}
                  disableGutters
                  elevation={0}
                  sx={{
                    ...panelSx,
                    overflow: "hidden",

                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <MuiAccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: COLORS.primary }} />}
                    sx={{
                      minHeight: 58,
                      px: { xs: 1.5, md: 1.8 },
                      bgcolor: COLORS.surface,

                      "& .MuiAccordionSummary-content": {
                        my: 0.8,
                        alignItems: "center",
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.2}
                      alignItems="center"
                      sx={{ width: "100%", minWidth: 0 }}
                    >
                      <Avatar
                        sx={{
                          width: 38,
                          height: 38,
                          bgcolor: typeConfig.bg,
                          color: typeConfig.color,
                          border: `1px solid ${typeConfig.color}22`,
                        }}
                      >
                        {typeConfig.icon}
                      </Avatar>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          noWrap
                          sx={{
                            color: COLORS.textStrong,
                            fontWeight: TYPO.pageTitle,
                            fontSize: "0.9rem",
                            fontFamily: FONT_FAMILY,
                          }}
                        >
                          {group.name}
                        </Typography>

                        <Typography
                          sx={{
                            color: COLORS.muted,
                            fontSize: "0.7rem",
                            fontWeight: TYPO.meta,
                            fontFamily: FONT_FAMILY,
                          }}
                        >
                          {group.tasks.length} tarea{group.tasks.length !== 1 ? "s" : ""} recomendada
                          {group.tasks.length !== 1 ? "s" : ""}
                        </Typography>
                      </Box>

                      <Chip
                        label={`${group.tasks.length}`}
                        size="small"
                        sx={{
                          bgcolor: typeConfig.bg,
                          color: typeConfig.color,
                          border: `1px solid ${typeConfig.color}22`,
                          fontWeight: TYPO.chip,
                          fontFamily: FONT_FAMILY,
                        }}
                      />
                    </Stack>
                  </MuiAccordionSummary>

                  <MuiAccordionDetails sx={{ p: 0, borderTop: `1px solid ${COLORS.border}` }}>
                    <List dense disablePadding>
                      {group.tasks.map((task, taskIndex) => (
                        <TaskItem
                          key={task.id || task._id || `${group.name}-${taskIndex}`}
                          task={task}
                          taskIndex={taskIndex}
                          userRole={userRole}
                          isExpanded={
                            expandedTaskPanel === getTaskStableId(task, `${group.name}-${taskIndex}`)
                          }
                          onAccordionChange={handleTaskAccordionChange(
                            getTaskStableId(task, `${group.name}-${taskIndex}`),
                            task
                          )}
                          description={task.descripcion}
                          isLoading={false}
                          error={null}
                          typeConfig={typeConfig}
                        />
                      ))}
                    </List>
                  </MuiAccordionDetails>
                </MuiAccordion>
              );
            })}
          </Stack>
        )}
      </List>
    </Box>
  );
};

const TaskItem = ({
  task,
  taskIndex,
  userRole,
  isExpanded,
  onAccordionChange,
  description,
  isLoading,
  error,
  typeConfig,
}) => {
  if (userRole === "ROLE_FREEMIUM") {
    return (
      <ListItem
        sx={{
          px: { xs: 1.5, md: 2 },
          py: 1.4,
          borderBottom: `1px solid ${COLORS.borderSoft}`,
        }}
      >
        <Stack direction="row" spacing={1.1} alignItems="flex-start">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "10px",
              display: "grid",
              placeItems: "center",
              bgcolor: typeConfig.bg,
              color: typeConfig.color,
              fontWeight: TYPO.pageTitle,
              fontSize: "0.72rem",
              flexShrink: 0,
            }}
          >
            {taskIndex + 1}
          </Box>

          <Typography
            sx={{
              color: COLORS.text,
              fontWeight: TYPO.bodyStrong,
              fontSize: "0.78rem",
              lineHeight: 1.55,
              fontFamily: FONT_FAMILY,
            }}
          >
            {getTaskTitle(task)}
          </Typography>
        </Stack>
      </ListItem>
    );
  }

  const parsedContent = parseDetailedContent(description || task.descripcion);
  const mainDescription =
    cleanTaskDescription(
      cleanEmbeddedSectionsFromDescription(task.descripcion || parsedContent.mainDescription)
    ) || "No hay descripción disponible.";
  const steps = Array.isArray(task.pasos) && task.pasos.length ? task.pasos : parsedContent.steps;
  const evidencias = Array.isArray(task.evidencias) ? task.evidencias : [];
  const responsables = Array.isArray(task.responsablesSugeridos) ? task.responsablesSugeridos : [];

  return (
    <MuiAccordion
      id={`task-detail-${getTaskStableId(task)}`}
      expanded={isExpanded}
      onChange={onAccordionChange}
      disableGutters
      elevation={0}
      sx={{
        boxShadow: "none",
        bgcolor: COLORS.surface,

        "&:before": {
          display: "none",
        },

        "&:not(:last-of-type)": {
          borderBottom: `1px solid ${COLORS.borderSoft}`,
        },
      }}
    >
      <MuiAccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: COLORS.primary }} />}
        sx={{
          minHeight: "auto",
          px: { xs: 1.4, md: 1.8 },

          "& .MuiAccordionSummary-content": {
            my: 1.2,
          },
        }}
      >
        <Stack direction="row" spacing={1.1} alignItems="flex-start" sx={{ width: "100%" }}>
          <Chip
            label={`Tarea ${taskIndex + 1}`}
            size="small"
            sx={{
              height: 24,
              bgcolor: typeConfig.bg,
              color: typeConfig.color,
              fontWeight: TYPO.chip,
              border: `1px solid ${typeConfig.color}22`,
              fontSize: "0.66rem",
              fontFamily: FONT_FAMILY,
              flexShrink: 0,
            }}
          />

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                color: COLORS.textStrong,
                fontWeight: TYPO.bodyStrong,
                fontSize: "0.8rem",
                lineHeight: 1.5,
                fontFamily: FONT_FAMILY,
              }}
            >
              {getTaskTitle(task)}
            </Typography>
          </Box>
        </Stack>
      </MuiAccordionSummary>

      <MuiAccordionDetails
        sx={{
          px: { xs: 1.7, md: 4 },
          pb: 2,
          pt: 0,
        }}
      >
        {isLoading ? (
          <Stack
            direction="row"
            spacing={1.2}
            alignItems="center"
            sx={{
              p: 1.4,
              borderRadius: "16px",
              bgcolor: COLORS.primarySoft,
              color: COLORS.primary,
            }}
          >
            <CircularProgress size={18} sx={{ color: COLORS.primary }} />

            <Typography
              sx={{
                fontSize: "0.76rem",
                fontWeight: TYPO.meta,
                fontFamily: FONT_FAMILY,
              }}
            >
              Cargando descripción premium...
            </Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: "16px" }}>
            {error}
          </Alert>
        ) : mainDescription ? (
          <Box
            sx={{
              p: 1.6,
              borderRadius: "18px",
              bgcolor: COLORS.surfaceSoft,
              border: `1px solid ${COLORS.border}`,
            }}
          >
            <Typography
              sx={{
                color: COLORS.textStrong,
                fontWeight: TYPO.sectionTitle,
                fontSize: "0.78rem",
                mb: 0.45,
                fontFamily: FONT_FAMILY,
              }}
            >
              Descripción recomendada
            </Typography>

            <Typography
              sx={{
                color: COLORS.text,
                fontSize: "0.76rem",
                lineHeight: 1.65,
                fontWeight: TYPO.body,
                fontFamily: FONT_FAMILY,
              }}
            >
              {mainDescription}
            </Typography>

            {userRole === "ROLE_PREMIUMPRO" && steps.length > 0 && (
              <Box sx={{ mt: 1.6 }}>
                <Typography
                  sx={{
                    color: COLORS.textStrong,
                    fontWeight: TYPO.sectionTitle,
                    fontSize: "0.78rem",
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Pasos de implementación
                </Typography>

                <Stack spacing={0.8}>
                  {steps.map((step, index) => (
                    <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "9px",
                          display: "grid",
                          placeItems: "center",
                          bgcolor: COLORS.primarySoft,
                          color: COLORS.primary,
                          fontWeight: TYPO.chip,
                          fontSize: "0.66rem",
                          flexShrink: 0,
                          fontFamily: FONT_FAMILY,
                        }}
                      >
                        {index + 1}
                      </Box>

                      <Typography
                        sx={{
                          color: COLORS.text,
                          fontSize: "0.74rem",
                          lineHeight: 1.55,
                          fontWeight: TYPO.body,
                          fontFamily: FONT_FAMILY,
                        }}
                      >
                        {step}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            {userRole === "ROLE_PREMIUMPRO" && evidencias.length > 0 && (
              <Box sx={{ mt: 1.6 }}>
                <Typography
                  sx={{
                    color: COLORS.textStrong,
                    fontWeight: TYPO.sectionTitle,
                    fontSize: "0.78rem",
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Evidencias esperadas
                </Typography>

                <Stack spacing={0.6}>
                  {evidencias.map((item, index) => (
                    <Stack key={index} direction="row" spacing={0.9} alignItems="flex-start">
                      <CheckCircleOutlineIcon
                        sx={{ color: COLORS.success, fontSize: 17, mt: "2px" }}
                      />

                      <Typography
                        sx={{
                          color: COLORS.text,
                          fontSize: "0.74rem",
                          lineHeight: 1.55,
                          fontWeight: TYPO.body,
                          fontFamily: FONT_FAMILY,
                        }}
                      >
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            {userRole === "ROLE_PREMIUMPRO" && responsables.length > 0 && (
              <Box sx={{ mt: 1.6 }}>
                <Typography
                  sx={{
                    color: COLORS.textStrong,
                    fontWeight: TYPO.sectionTitle,
                    fontSize: "0.78rem",
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Responsables sugeridos
                </Typography>

                <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
                  {responsables.map((item, index) => (
                    <Chip
                      key={index}
                      label={item}
                      size="small"
                      sx={{
                        bgcolor: COLORS.primarySoft,
                        color: COLORS.primary,
                        border: `1px solid ${COLORS.primary}22`,
                        fontWeight: TYPO.chip,
                        fontSize: "0.66rem",
                        fontFamily: FONT_FAMILY,
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        ) : (
          <Typography
            sx={{
              color: COLORS.muted,
              fontSize: "0.76rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            Abre esta tarea para generar una descripción detallada.
          </Typography>
        )}
      </MuiAccordionDetails>
    </MuiAccordion>
  );
};

const StatCard = ({ title, value, icon, color, bg }) => (
  <Paper
    elevation={0}
    sx={{
      ...panelSx,
      p: 1.6,
      height: "100%",
      transition: "all 180ms ease",

      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: COLORS.hoverShadow,
        borderColor: `${color}44`,
      },
    }}
  >
    <Stack direction="row" spacing={1.2} alignItems="center">
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: bg,
          color,
          border: `1px solid ${color}22`,
        }}
      >
        {icon}
      </Avatar>

      <Box>
        <Typography
          sx={{
            color,
            fontSize: "1.45rem",
            fontWeight: TYPO.pageTitle,
            lineHeight: 1,
            letterSpacing: "-0.045em",
            fontFamily: FONT_FAMILY,
          }}
        >
          {value}
        </Typography>

        <Typography
          sx={{
            color: COLORS.muted,
            fontSize: "0.7rem",
            fontWeight: TYPO.meta,
            fontFamily: FONT_FAMILY,
          }}
        >
          {title}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const EmptyState = ({ icon, title, text }) => (
  <Paper
    elevation={0}
    sx={{
      ...panelSx,
      p: { xs: 3, md: 4 },
      textAlign: "center",
    }}
  >
    <Avatar
      sx={{
        width: 54,
        height: 54,
        mx: "auto",
        mb: 1.4,
        bgcolor: COLORS.primarySoft,
        color: COLORS.primary,
      }}
    >
      {icon}
    </Avatar>

    <Typography
      sx={{
        color: COLORS.textStrong,
        fontWeight: TYPO.pageTitle,
        fontSize: "1rem",
        fontFamily: FONT_FAMILY,
      }}
    >
      {title}
    </Typography>

    <Typography
      sx={{
        color: COLORS.muted,
        fontSize: "0.78rem",
        lineHeight: 1.55,
        mt: 0.5,
        fontFamily: FONT_FAMILY,
      }}
    >
      {text}
    </Typography>
  </Paper>
);

const PageLoading = () => (
  <Box
    sx={{
      minHeight: "100vh",
      background: COLORS.background,
      display: "grid",
      placeItems: "center",
      p: 3,
      fontFamily: FONT_FAMILY,
    }}
  >
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        p: 4,
        textAlign: "center",
        maxWidth: 390,
      }}
    >
      <CircularProgress sx={{ color: COLORS.primary, mb: 2 }} />

      <Typography
        sx={{
          color: COLORS.textStrong,
          fontWeight: TYPO.pageTitle,
          fontSize: "1.05rem",
          fontFamily: FONT_FAMILY,
        }}
      >
        Cargando plan de mejora
      </Typography>

      <Typography
        sx={{
          color: COLORS.muted,
          fontWeight: TYPO.body,
          mt: 0.75,
          fontSize: "0.8rem",
          lineHeight: 1.6,
          fontFamily: FONT_FAMILY,
        }}
      >
        Preparando las recomendaciones y tareas priorizadas.
      </Typography>
    </Paper>
  </Box>
);

const PropuestasMejoraPage = () => {
  const { cuestionarioId } = useParams();
  const navigate = useNavigate();

  const [userRole, setUserRole] = useState(null);
  const [loadingRole, setLoadingRole] = useState(true);

  useEffect(() => {
    document.body.style.background = COLORS.page;
  }, []);

  useEffect(() => {
    const token = getAuthToken();

    if (token) {
      try {
        setUserRole(jwtDecode(token).role);
      } catch (e) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }

    setLoadingRole(false);
  }, [navigate]);

  if (loadingRole) return <PageLoading />;

  const isPremiumPro = userRole === "ROLE_PREMIUMPRO";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: COLORS.background,
        pt: { xs: 8.5, md: 2.5 },
        pb: 6,
        fontFamily: FONT_FAMILY,
        color: COLORS.text,
      }}
    >
      <Box sx={{ px: { xs: 1.25, md: 2 }, maxWidth: "100%", mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            ...panelSx,
            px: { xs: 1.5, md: 2 },
            py: 1.3,
            mb: 2,
            position: "sticky",
            top: { xs: 72, md: 12 },
            zIndex: 20,
            bgcolor: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(18px)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            spacing={1.2}
          >
            <Button
              startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
              onClick={() => navigate(-1)}
              sx={{
                ...secondaryButtonSx,
                alignSelf: { xs: "flex-start", md: "center" },
              }}
            >
              Volver a resultados
            </Button>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: COLORS.accentSoft,
                  color: COLORS.accent,
                  border: `1px solid ${COLORS.accent}22`,
                }}
              >
                <AutoFixHighIcon sx={{ fontSize: 19 }} />
              </Avatar>

              <Box>
                <Typography
                  sx={{
                    color: COLORS.textStrong,
                    fontWeight: TYPO.pageTitle,
                    fontSize: "0.95rem",
                    letterSpacing: "-0.025em",
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Plan de mejora detallado
                </Typography>

                <Typography
                  sx={{
                    color: COLORS.muted,
                    fontWeight: TYPO.body,
                    fontSize: "0.7rem",
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Recomendaciones accionables para avanzar en madurez
                </Typography>
              </Box>
            </Stack>

            <Chip
              icon={<WorkspacePremiumIcon sx={{ fontSize: 16 }} />}
              label={
                userRole === "ROLE_PREMIUMPRO"
                  ? "Premium Pro"
                  : userRole === "ROLE_PREMIUM"
                    ? "Premium"
                    : "Freemium"
              }
              size="small"
              sx={{
                height: 26,
                bgcolor: COLORS.primarySoft,
                color: COLORS.primary,
                border: `1px solid ${COLORS.primary}22`,
                fontWeight: TYPO.chip,
                fontSize: "0.68rem",
                fontFamily: FONT_FAMILY,
                alignSelf: { xs: "flex-start", md: "center" },
              }}
            />
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            ...panelSx,
            p: { xs: 1.6, md: 2.3 },
            mb: 2.3,
            position: "relative",
            overflow: "hidden",
            background:
              "radial-gradient(circle at top right, rgba(211,125,54,0.11), transparent 32%), linear-gradient(135deg, #FFFFFF 0%, #F8FBFF 100%)",
          }}
        >
          <Grid container spacing={2.2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Propuestas de mejora"
                size="small"
                sx={{
                  height: 24,
                  mb: 1,
                  bgcolor: COLORS.accentSoft,
                  color: COLORS.accent,
                  border: `1px solid ${COLORS.accent}22`,
                  fontWeight: TYPO.chip,
                  fontSize: "0.68rem",
                  fontFamily: FONT_FAMILY,
                }}
              />

              <Typography
                sx={{
                  color: COLORS.textStrong,
                  fontWeight: TYPO.pageTitle,
                  fontSize: { xs: "1.45rem", md: "1.95rem" },
                  letterSpacing: "-0.055em",
                  lineHeight: 1.08,
                  fontFamily: FONT_FAMILY,
                }}
              >
                Convierte los resultados en un plan de acción
              </Typography>

              <Typography
                sx={{
                  color: COLORS.muted,
                  fontWeight: TYPO.body,
                  fontSize: { xs: "0.8rem", md: "0.88rem" },
                  lineHeight: 1.65,
                  mt: 0.9,
                  maxWidth: 820,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {isPremiumPro
                  ? "Consulta las tareas recomendadas, explora descripciones premium y prioriza acciones por área, proceso y horizonte temporal."
                  : "En base a tus respuestas, te proponemos a alto nivel una serie de propuestas de mejora para orientar los próximos pasos de evolución en gobierno, gestión y calidad del dato."}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.6,
                  borderRadius: "20px",
                  bgcolor: COLORS.surface,
                  border: `1px solid ${COLORS.border}`,
                }}
              >
                <Stack spacing={1.1}>
                  <HeroPoint
                    icon={<InsightsRoundedIcon />}
                    title="Diagnóstico accionable"
                    text="Cada tarea está vinculada a procesos concretos."
                  />

                  <HeroPoint
                    icon={<RestartAltRoundedIcon />}
                    title="Mejora continua"
                    text={
                      isPremiumPro
                        ? "Organiza el avance por corto, medio y largo plazo."
                        : "Disponible en planes superiores."
                    }
                    disabled={!isPremiumPro}
                  />

                  <HeroPoint
                    icon={<AutoFixHighIcon />}
                    title="Apoyo con IA"
                    text={
                      isPremiumPro
                        ? "Descripciones y pasos para facilitar la ejecución."
                        : "Disponible en planes superiores."
                    }
                    disabled={!isPremiumPro}
                  />
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            ...panelSx,
            p: { xs: 1.6, md: 2.4 },
          }}
        >
          {isPremiumPro && (
            <>
              <TimeframeRoadmap cuestionarioId={cuestionarioId} />
              <Divider sx={{ my: 3, borderColor: COLORS.border, borderStyle: "dashed" }} />
            </>
          )}

          <DetailedTasksView cuestionarioId={cuestionarioId} userRole={userRole} />
        </Paper>
      </Box>
    </Box>
  );
};

const HeroPoint = ({ icon, title, text, disabled = false }) => (
  <Stack
    direction="row"
    spacing={1}
    alignItems="flex-start"
    sx={{
      opacity: disabled ? 0.55 : 1,
      filter: disabled ? "grayscale(0.25)" : "none",
      transition: "all 180ms ease",
    }}
  >
    <Avatar
      sx={{
        width: 32,
        height: 32,
        bgcolor: disabled ? COLORS.surfaceSoft : COLORS.primarySoft,
        color: disabled ? COLORS.mutedSoft : COLORS.primary,
        border: disabled
          ? `1px solid ${COLORS.borderStrong}`
          : `1px solid ${COLORS.primary}22`,

        "& svg": {
          fontSize: 18,
        },
      }}
    >
      {icon}
    </Avatar>

    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
        <Typography
          sx={{
            color: disabled ? COLORS.muted : COLORS.textStrong,
            fontWeight: TYPO.sectionTitle,
            fontSize: "0.78rem",
            fontFamily: FONT_FAMILY,
          }}
        >
          {title}
        </Typography>

        {disabled && (
          <Chip
            label="Plan superior"
            size="small"
            sx={{
              height: 19,
              bgcolor: COLORS.surfaceSoft,
              color: COLORS.muted,
              border: `1px solid ${COLORS.border}`,
              fontSize: "0.58rem",
              fontWeight: TYPO.chip,
              fontFamily: FONT_FAMILY,
            }}
          />
        )}
      </Stack>

      <Typography
        sx={{
          color: disabled ? COLORS.mutedSoft : COLORS.muted,
          fontWeight: TYPO.body,
          fontSize: "0.7rem",
          lineHeight: 1.45,
          fontFamily: FONT_FAMILY,
          mt: 0.1,
        }}
      >
        {text}
      </Typography>
    </Box>
  </Stack>
);

export default PropuestasMejoraPage;
